import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Globe, Facebook, Download, Share2, Check, ArrowLeft, Sparkles, ImageDown } from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

const CarteVisite = () => {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  // Infos entreprise
  const entreprise = {
    nom: 'GAP-VO',
    slogan: 'Véhicules d\'occasion',
    telephone: '+33667713749',
    telephoneAffiche: '+33 6 67 71 37 49',
    site: 'https://gap-vo-affaires.fr',
    facebook: 'https://www.facebook.com/share/1FEJfG9TN1/',
    tiktok: 'https://www.tiktok.com/@gap.ou.pas.gap',
  };

  // URL du QR code TikTok (via api.qrserver.com — gratuit, sans clé)
  const qrTikTok = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&margin=10&data=${encodeURIComponent(entreprise.tiktok)}`;

  const downloadVCard = () => {
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${entreprise.nom}`,
      `ORG:${entreprise.nom}`,
      `TITLE:${entreprise.slogan}`,
      `TEL;TYPE=CELL:${entreprise.telephone}`,
      `URL:${entreprise.site}`,
      `URL;TYPE=Facebook:${entreprise.facebook}`,
      `URL;TYPE=TikTok:${entreprise.tiktok}`,
      'END:VCARD',
    ].join('\n');

    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'GAP-VO.vcf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Carte de visite ajoutée à vos contacts');
  };

  const shareCard = async () => {
    const base = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    const url = `${base}/carte`;
    const text = `Découvrez ${entreprise.nom} — ${entreprise.slogan}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: entreprise.nom, text, url });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Lien de la carte copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Impossible de copier le lien');
    }
  };

  const downloadImage = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 3, // Haute résolution pour impression
        useCORS: true,
        allowTaint: false,
      });
      const link = document.createElement('a');
      link.download = 'carte-visite-GAP-VO.png';
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Carte téléchargée en image');
    } catch (err) {
      console.error(err);
      toast.error('Impossible de télécharger la carte');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="carte-visite-page">
      {/* Halos décoratifs */}
      <div className="pointer-events-none absolute top-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl" />

      {/* Bouton retour discret */}
      <Link
        to="/catalogue"
        data-testid="back-to-catalogue"
        className="absolute top-4 left-4 flex items-center gap-2 text-slate-300 hover:text-white text-sm transition-colors z-10"
      >
        <ArrowLeft size={16} />
        Retour au catalogue
      </Link>

      {/* Carte principale */}
      <div ref={cardRef} className="relative w-full max-w-md bg-gradient-to-br from-white to-slate-50 rounded-3xl shadow-2xl overflow-hidden border-2 border-red-600/40">
        {/* Bandeau top rouge */}
        <div className="h-3 bg-gradient-to-r from-red-600 via-red-500 to-red-600" />

        <div className="px-8 py-8 relative">
          {/* Icône Sparkles décorative */}
          <Sparkles className="absolute top-6 right-6 text-red-500 opacity-60" size={20} />

          {/* Nom société */}
          <div className="text-center mb-2">
            <img
              src="https://customer-assets.emergentagent.com/job_auto-flip-tracker-1/artifacts/mote16qp_26609.jpg"
              alt="GAP-VO"
              className="h-24 mx-auto mb-3 object-contain"
            />
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              {entreprise.nom}
            </h1>
            <p className="text-red-600 font-semibold italic mt-1">{entreprise.slogan}</p>
          </div>

          {/* Séparateur */}
          <div className="my-6 h-px bg-gradient-to-r from-transparent via-red-300 to-transparent" />

          {/* Coordonnées */}
          <div className="space-y-3 mb-6">
            <a
              href={`tel:${entreprise.telephone}`}
              data-testid="carte-tel"
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-red-50 border border-slate-200 hover:border-red-300 transition-all group"
            >
              <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Phone size={18} />
              </div>
              <div className="flex-1">
                <p className="text-xs text-slate-500">Téléphone</p>
                <p className="text-slate-900 font-semibold font-mono">{entreprise.telephoneAffiche}</p>
              </div>
            </a>

            <a
              href={entreprise.site}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="carte-site"
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-red-50 border border-slate-200 hover:border-red-300 transition-all group"
            >
              <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Globe size={18} />
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="text-xs text-slate-500">Site web</p>
                <p className="text-slate-900 font-semibold truncate">gap-vo-affaires.fr</p>
              </div>
            </a>

            <a
              href={entreprise.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="carte-facebook"
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 transition-all group"
            >
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Facebook size={18} className="fill-current" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-slate-500">Facebook</p>
                <p className="text-slate-900 font-semibold">Notre page</p>
              </div>
            </a>
          </div>

          {/* QR TikTok */}
          <div className="bg-black text-white rounded-2xl p-5 mb-6" data-testid="qr-tiktok-block">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Scannez pour</p>
                <p className="text-lg font-bold flex items-center gap-2">
                  Suivez notre TikTok
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
                  </svg>
                </p>
              </div>
            </div>
            <div className="flex justify-center bg-white rounded-xl p-3">
              <img
                src={qrTikTok}
                alt="QR Code TikTok"
                data-testid="qr-code-image"
                className="w-48 h-48"
              />
            </div>
            <p className="text-center text-xs text-slate-400 mt-2">@gap.ou.pas.gap</p>
          </div>

          {/* CTA */}
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={downloadVCard}
              data-testid="vcard-download-btn"
              className="flex flex-col items-center justify-center gap-1 px-2 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl text-xs"
            >
              <Download size={16} />
              <span>Contacts</span>
            </button>
            <button
              onClick={downloadImage}
              disabled={downloading}
              data-testid="image-download-btn"
              className="flex flex-col items-center justify-center gap-1 px-2 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl text-xs disabled:opacity-60"
            >
              <ImageDown size={16} />
              <span>{downloading ? '...' : 'Image'}</span>
            </button>
            <button
              onClick={shareCard}
              data-testid="share-card-btn"
              className="flex flex-col items-center justify-center gap-1 px-2 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl text-xs"
            >
              {copied ? <Check size={16} /> : <Share2 size={16} />}
              <span>{copied ? 'Copié' : 'Partager'}</span>
            </button>
          </div>
        </div>

        {/* Bandeau bas */}
        <div className="h-3 bg-gradient-to-r from-red-600 via-red-500 to-red-600" />
      </div>
    </div>
  );
};

export default CarteVisite;
